import { formatCLP } from '../../../Facturacion/utils/formatCLP'

const resolveCompanyValue = (currentBusiness, keys, fallback = '') => {
  for (const key of keys) {
    const value = currentBusiness?.[key]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value)
    }
  }

  return fallback
}

const formatDateValue = value => {
  if (!value) return '-'

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return String(value)

  return parsedDate.toLocaleDateString('es-CL')
}

const getLineSubtotal = line => {
  const quantity = Number(line?.quantity ?? 0) || 0
  const unitPrice = Number(line?.unitPrice ?? 0) || 0
  const discountPercent = Number(line?.discountPercent ?? 0) || 0
  const gross = quantity * unitPrice
  const discount = (gross * discountPercent) / 100

  return gross - discount
}

const PreviewField = ({ label, value }) => (
  <div>
    <p className='mb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500'>
      {label}
    </p>
    <div className='flex min-h-[42px] items-center rounded-[10px] border border-slate-200 bg-white px-3 text-sm text-slate-700'>
      {value || '-'}
    </div>
  </div>
)

const SalesNotePreviewDocument = ({ currentBusiness, note, totals }) => {
  const visibleLines = (note?.lines || []).filter(
    line => String(line?.itemName || '').trim() || String(line?.itemCode || '').trim()
  )

  const companyName = resolveCompanyValue(
    currentBusiness,
    ['business_name', 'businessName', 'name', 'nombre'],
    'Empresa'
  )
  const companyRut = resolveCompanyValue(currentBusiness, ['rut', 'tax_id', 'taxId'])
  const companyAddress = resolveCompanyValue(currentBusiness, ['address', 'direccion'])
  const companyDistrict = resolveCompanyValue(currentBusiness, ['district', 'comuna'])
  const companyCity = resolveCompanyValue(currentBusiness, ['city', 'ciudad'])

  return (
    <div className='mx-auto max-w-[1180px] space-y-4 bg-[#f4f7fb] p-2'>
      <div className='overflow-hidden rounded-[20px] border border-[#d5eceb] bg-white shadow-[0_14px_36px_rgba(15,23,42,0.08)]'>
        <div className='bg-[#82cfca] px-4 py-2 text-[11px] font-medium text-white'>
          Dirección de Empresa, Datos de Contacto, Título y Logo
        </div>

        <div className='grid gap-5 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_300px]'>
          <div className='min-h-[142px] rounded-[14px] border border-dashed border-[#82cfca] bg-[#fbfefe] px-5 py-4'>
            <p className='text-[10px] font-semibold uppercase tracking-[0.14em] text-[#63bdb8]'>
              Empresa emisora
            </p>
            <h3 className='mt-4 text-2xl font-semibold text-slate-700'>{companyName}</h3>
            <div className='mt-4 space-y-1.5 text-sm text-slate-500'>
              {companyRut ? <p>{companyRut}</p> : null}
              {companyAddress ? <p>{companyAddress}</p> : null}
              {companyDistrict || companyCity ? (
                <p>{[companyDistrict, companyCity].filter(Boolean).join(', ')}</p>
              ) : null}
            </div>
          </div>

          <div className='flex flex-col items-end justify-between gap-8 text-right'>
            <div className='pt-2 text-[20px] leading-none text-slate-700 md:text-[22px]'>
              <span className='font-light'>Nota de Venta</span>
              <span className='font-light text-slate-500'>
                #{String(note?.internalCode || 'BORRADOR').replace('NV-', '')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='overflow-hidden rounded-[20px] border border-[#9ed9d7] bg-white shadow-[0_14px_36px_rgba(15,23,42,0.08)]'>
        <div className='grid gap-4 p-4 xl:grid-cols-[132px_minmax(0,1fr)_230px]'>
          <section className='rounded-[14px] border border-slate-200 bg-white px-3 py-4 text-center shadow-[0_4px_16px_rgba(15,23,42,0.08)]'>
            <div className='mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 text-slate-400'>
              <span className='text-2xl'>+</span>
            </div>
            <p className='mt-4 text-[11px] font-medium leading-4 text-slate-600'>
              Seleccionar o Crear Cliente
            </p>
          </section>

          <section className='grid gap-3 md:grid-cols-2'>
            <PreviewField label='Cliente' value={note?.customer?.businessName} />
            <PreviewField label='RUT' value={note?.customer?.rut} />
            <PreviewField label='Contacto' value={note?.customer?.contact} />
            <PreviewField label='Dirección' value={note?.customer?.address} />
            <PreviewField label='Comuna' value={note?.customer?.district} />
            <PreviewField label='Ciudad' value={note?.customer?.city} />
          </section>

          <section className='space-y-3'>
            <PreviewField label='Fecha de Emisión' value={formatDateValue(note?.dateIssued)} />
            <PreviewField label='Fecha de Vencimiento' value={formatDateValue(note?.dueDate)} />
          </section>
        </div>

        <div className='border-t border-[#d9eceb]'>
          <div className='overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead className='bg-[#f3f5f6] text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500'>
                <tr>
                  <th className='px-4 py-3 text-left'>Código</th>
                  <th className='px-4 py-3 text-left'>Producto</th>
                  <th className='px-4 py-3 text-left'>Cantidad</th>
                  <th className='px-4 py-3 text-left'>Precio</th>
                  <th className='px-4 py-3 text-left'>Descuento</th>
                  <th className='px-4 py-3 text-right'>Monto</th>
                </tr>
              </thead>

              <tbody>
                {visibleLines.length ? (
                  visibleLines.map((line, index) => (
                    <tr
                      key={`${line.itemCode || line.itemName || 'line'}-${index}`}
                      className='border-t border-[#e8ecef] align-top'
                    >
                      <td className='px-4 py-4 text-slate-500'>{line.itemCode || '-'}</td>
                      <td className='px-4 py-4'>
                        <div className='font-medium text-slate-700'>{line.itemName || '-'}</div>
                        {line.itemDescription ? (
                          <div className='mt-1 text-xs text-slate-500'>{line.itemDescription}</div>
                        ) : null}
                      </td>
                      <td className='px-4 py-4 text-slate-700'>
                        {Number(line.quantity || 0)} {line.unit || ''}
                      </td>
                      <td className='px-4 py-4 text-slate-700'>{formatCLP(line.unitPrice || 0)}</td>
                      <td className='px-4 py-4 text-slate-700'>
                        {Number(line.discountPercent || 0) ? `${line.discountPercent}%` : '-'}
                      </td>
                      <td className='px-4 py-4 text-right font-semibold text-slate-800'>
                        {formatCLP(getLineSubtotal(line))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className='border-t border-[#e8ecef]'>
                    <td colSpan={6} className='px-4 py-8 text-center text-sm text-slate-400'>
                      No hay items en esta nota de venta.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className='grid gap-4 border-t border-[#d9eceb] p-4 lg:grid-cols-[minmax(0,1fr)_270px]'>
          <section className='space-y-4'>
            <div>
              <p className='mb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500'>
                Notas
              </p>
              <div className='min-h-[120px] rounded-[12px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700'>
                {note?.note || '-'}
              </div>
            </div>
          </section>

          <div className='rounded-[16px] border border-[#d7ebea] bg-white px-5 py-4'>
            <div className='space-y-3 text-sm text-slate-700'>
              <div className='flex items-center justify-between'>
                <span>Sub-Total</span>
                <span>{formatCLP(totals?.net || 0)}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span>Nota exenta</span>
                <span>{note?.isExempt ? 'Sí' : 'No'}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span>IVA (19%)</span>
                <span>{note?.isExempt ? 'No aplica' : formatCLP(totals?.iva || 0)}</span>
              </div>
              <div className='flex items-center justify-between border-t border-slate-200 pt-3 text-base font-semibold text-slate-900'>
                <span>Total</span>
                <span>{formatCLP(totals?.total || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesNotePreviewDocument
